//#region types
import { FormEnums } from "models/enums";
import { CommonConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { CustomerFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
import { useCustomerService } from "serviceHooks";
import { FilterHelper, LocalizationHelperInstance, QueryStateHelper } from "utils/helpers";

function useCustomerReportFilterDefinition() {
  const customerService = useCustomerService();
  const searchOrgNr = async (value: string, formattedValue?: string) => {
    if (!formattedValue) return [];
    return customerService.searchCustomersByOrgNr(formattedValue).then((res) => {
      if (!res) return [];
      const options: AutocompleteListItem[] = res?.map((item) => ({
        id: item.orgNr ?? 0,
        value: item.orgNr ?? "",
        description: item.name ?? "",
        icon: IconDefinitionConstants.WBuilding,
        group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      }));

      return options;
    });
  };

  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.CustomerFilter) => {
    const reportFilter: CustomerFormTypes.Filter = {};

    if (filter.name !== undefined) {
      reportFilter.customerName = filter.name;
    }

    if (filter.orgNumber !== undefined) {
      reportFilter.orgNumber = filter.orgNumber;
    }

    reportFilter.active = !filter.inactive;

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.Rating,
      filter.ratingFrom,
      filter.ratingTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.Date,
      filter.invoiceDateFrom,
      filter.invoiceDateTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.NumOfInvoices,
      filter.numOfInvoicesFrom,
      filter.numOfInvoicesTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.InvoicedAmount,
      filter.invoicedAmountFrom,
      filter.invoicedAmountTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.UnpaidAmount,
      filter.unpaidAmountFrom,
      filter.unpaidAmountTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Customer.Filter.NumOfRecurringInvoices,
      filter.numOfRecurringInvoicesFrom,
      filter.numOfRecurringInvoicesTo
    );

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<CustomerFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        orgNumber: {
          label: TranslationKeys.Common.CIN,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Customer.Filter.OrgNumber,
            items: searchOrgNr,
            mask: CommonConstants.OrganizationNumberMask,
            isNumericString: true,
            formatInputValue: (value?: string) => value?.replace("-", ""),
          } as AutoCompleteExtraProps<CustomerFormTypes.Filter>,
        },
        rating: {
          label: TranslationKeys.Common.Rating,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        active: {
          label: TranslationKeys.Common.Active,
          type: FormEnums.FormFieldType.BOOLEANSELECTION,
        },
      },
    },
    {
      label: TranslationKeys.Common.Invoicing,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        date: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        numOfInvoices: {
          label: TranslationKeys.Common.NumOfInvoices,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        invoicedAmount: {
          label: TranslationKeys.Common.InvoicedAmount,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
        unpaidAmount: {
          label: TranslationKeys.Common.Unpaid,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
        numOfRecurringInvoices: {
          label: TranslationKeys.Common.NumOfRecurringInvoices,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<CustomerFormTypes.Filter> = {
    customerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Customer.Filter.CustomerName),
    orgNumber: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Customer.Filter.OrgNumber),
    rating: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Customer.Filter.Rating),
    date: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Customer.Filter.Date),
    numOfInvoices: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Customer.Filter.NumOfInvoices),
    invoicedAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Customer.Filter.InvoicedAmount),
    unpaidAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Customer.Filter.UnpaidAmount),
    numOfRecurringInvoices: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.Customer.Filter.NumOfRecurringInvoices
    ),
    active: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Customer.Filter.Active),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useCustomerReportFilterDefinition;
